import { z } from 'zod';
import {
  buildPaymentCardParamsFromForm,
  isAcceptedCardBrand,
  isCardExpired,
  isValidBillingZipCode,
  isValidCardNumber,
  isValidHolderIdentity,
  type PaymentCardParams,
  type PaymentOption,
} from '@src/domains/payments';
import { Translate } from '@src/i18n/useTranslate';

export const directLinkCardFormSchema = (
  t: Translate['i18n'],
  isForeignBuy: boolean,
  paymentOption?: PaymentOption,
) => {
  const requiredMessage = t('direct_link_required_field');

  const requiredStringField = z.string({ required_error: requiredMessage }).nonempty({ message: requiredMessage });
  const requiredForExternalAuthenticationField = z
    .string()
    .nullable()
    .refine((field) => {
      if (!paymentOption?.requiresExternalAuthentication) {
        return true;
      }

      return Boolean(field?.trim());
    }, requiredMessage);

  const billingAddressObject = z.object({
    zipCode: z
      .string()
      .optional()
      .refine(
        (zipCode) => isValidBillingZipCode(zipCode, isForeignBuy),
        t('direct_link_card_form_postal_code_must_be_valid'),
      ),
    state: requiredForExternalAuthenticationField,
    city: requiredForExternalAuthenticationField,
    district: requiredForExternalAuthenticationField,
    street: requiredForExternalAuthenticationField,
    number: requiredForExternalAuthenticationField,
    complement: z.string().nullable(),
  });

  return z
    .object({
      holder: requiredStringField,
      holderIdentity: z
        .string()
        .optional()
        .refine(
          (identity) => isValidHolderIdentity(identity, isForeignBuy),
          t('direct_link_card_form_identity_must_be_valid'),
        ),
      cardNumber: requiredStringField
        .refine((number) => isValidCardNumber(number), t('direct_link_card_form_card_number_must_be_valid'))
        .refine(
          (number) => isAcceptedCardBrand(number, paymentOption),
          t('direct_link_card_form_card_brand_not_accepted'),
        ),
      securityCode: requiredStringField,
      validity: requiredStringField.refine(
        (date) => !isCardExpired(date, new Date()),
        t('direct_link_card_form_card_expired'),
      ),
      installments: z.coerce.number().min(1),
      // The object carries the same condition as the fields inside it: without that, an absent
      // billingAddress skips their refinements and a 3DS payment validates with no address.
      billingAddress: paymentOption?.requiresExternalAuthentication
        ? billingAddressObject
        : billingAddressObject.optional(),
    })
    .transform((data): PaymentCardParams => buildPaymentCardParamsFromForm(data, isForeignBuy));
};
